// Shared types for the operator invoice-payment UI (CHO-754).
//
// These mirror the invoice-payments service contract (CHO-753, verified
// against invoice-payments@main). The service binds to 127.0.0.1 only and is
// reached through this app's server proxy at /api/payments, which forwards
// responses verbatim except payment attempts, which it maps from the service's
// snake_case rows to the camelCase shape below (server/routes/payments.ts —
// mapServiceAttempt), and the queue, where it attaches billerName per row
// (mapQueueBody — CHO-807; the service's field whitelist would drop it).
// Card references are last four digits ONLY; the service never exposes more,
// and this client must not accept, request, store, or display anything beyond
// that.

/**
 * Attempt lifecycle (invoice-payments src/constants.ts). `dry_run` is
 * terminal and only reachable when the service runs with DRY_RUN on — it
 * means the portal was walked but nothing was submitted, so it must NOT be
 * presented as "money may have left".
 */
export type PaymentAttemptStatus = 'initiated' | 'succeeded' | 'failed' | 'unknown' | 'dry_run';

/** Per-invoice eligibility as the service computes it (src/eligibility.ts). */
export interface InvoiceEligibility {
  payable: boolean;
  /** Free-form machine strings, e.g. `amount-exceeds-cap:AED 1,200.00 > cap AED 1,000.00`,
   *  `no-card-mapped: set CARD_PROPERTY_5`, `blocked:unknown attempt <id>`. */
  reasons: string[];
}

/** One row of GET /api/queue — a payable invoice with its eligibility. */
export interface PayableInvoice {
  /** airbnb-manager cost id (string). */
  id: string;
  propertyId: number;
  propertyName?: string;
  amount: number;
  currency: string;
  description?: string;
  /** Invoice date, when the source carries one. */
  date?: string;
  dueDate?: string;
  /** The Noqodi slug — machine payee selection, present only when configured. Never displayed as a name. */
  biller?: string;
  /**
   * Human display name for the biller (config label for the sender domain,
   * domain fallback), attached by the proxy (CHO-807). Display only — the
   * payment driver still pays via the `biller` slug and refuses unknown payees.
   */
  billerName?: string;
  /** The service decides payability; the UI never re-derives it. */
  eligibility: InvoiceEligibility;
}

/** A payment attempt as mapped by the proxy from the service's serialized row. */
export interface PaymentAttempt {
  id: string;
  invoiceId: string;
  /** Major units, as added by the service's serializeAttempt. */
  amount: number;
  currency: string;
  cardLast4: string;
  status: PaymentAttemptStatus;
  /** Human-readable refusal/ambiguity reason when status is `failed`/`unknown`. */
  failureReason: string | null;
  createdAt: string;
  /** Set only once a human has resolved the attempt. */
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolvedNote: string | null;
}

/**
 * Human resolution of an `unknown` attempt (or a crash-left `initiated` one),
 * after the operator checks Noqodi's own history. The service's resolve
 * endpoint takes `outcome: 'succeeded' | 'failed'` and REQUIRES a note.
 */
export type AttemptResolution = 'succeeded' | 'failed';

/**
 * Which card source is live for a property (invoice-payments CHO-763, the
 * card API this is read through — CHO-771). `ui` = set from this app and
 * stored service-side; `env` = the service's CARD_PROPERTY_<id> fallback is
 * effective; `none` = nothing is mapped, the property cannot be paid.
 */
export type PropertyCardSource = 'ui' | 'env' | 'none';

/**
 * The ONLY card shape that ever crosses the API (GET/PUT/DELETE
 * /api/payments/cards/:propertyId). Write-only: no endpoint ever returns a
 * PAN, expiry or CVV, so a value that can be written can never be read back.
 * `configuredAt` is null for env cards and when unmapped.
 */
export interface PropertyCardInfo {
  propertyId: number;
  /** Last four digits only — never a full number. */
  last4: string | null;
  configuredAt: string | null;
  source: PropertyCardSource;
}

/** PUT /api/payments/cards/:propertyId body — the three raw values, write-only. */
export interface SetPropertyCardInput {
  pan: string;
  expiry: string;
  cvv: string;
}

/**
 * GET /api/payments/payable-properties response (CHO-799). The ids of
 * properties the invoice payment service can actually pay — those with a
 * confirmed invoice_account_map row bound to a biller domain in the committed
 * allowlist. A property absent from this list gets no card slot on the
 * payments page.
 */
export interface PayablePropertiesResponse {
  propertyIds: number[];
}

/**
 * A UI-set card stored for a property the payment service can never pay
 * (no confirmed allowlisted mapping). Surfaced — never auto-deleted, never
 * silently hidden — as an orphaned-card row with a delete affordance and no
 * card form (CHO-799).
 */
export interface OrphanedCard {
  propertyId: number;
  propertyName: string;
  card: PropertyCardInfo;
}

/**
 * Friendly labels for the eligibility reason prefixes the service emits
 * (src/eligibility.ts). The prefix is the part before the first `:`; unknown
 * prefixes render verbatim — the UI shows whatever the service says.
 */
export const INELIGIBLE_REASON_LABELS: Record<string, string> = {
  'amount-exceeds-cap': 'Over the AED 1,000 per-payment cap',
  'blocked': 'Already attempted',
  'no-card-mapped': 'No card mapped for this property',
  'unsupported-currency': 'Unsupported currency',
};

export function formatIneligibleReason(reason: string): string {
  return INELIGIBLE_REASON_LABELS[reason.split(':')[0]] ?? reason;
}
